/**
 * Builds payout sidebar months from Nov 2024 through the last completed calendar month
 * (current month is omitted). `maxMonthsFromStart` is only a safety cap (not a target count);
 * a low value like 15 stops early and hides newer months (e.g. Feb/Mar 2026).
 */
export function generateMonthsWithFilterRange(maxMonthsFromStart = 240) {
    if (maxMonthsFromStart <= 0) {
        throw new Error("The max months cap must be greater than 0.");
    }

    // Local calendar Nov 2024 — NOT `new Date("2024-11-01")` (that is UTC midnight and breaks
    // `getMonth()` / `setMonth()` in US timezones, skipping e.g. February and duplicating months).
    let cursor = new Date(2024, 10, 1);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthIndex = currentDate.getMonth();
    // Only full/past months in payout — hide the running calendar month (e.g. April
    // after 1 Apr; the latest row is March).
    const startOfThisMonth = new Date(currentYear, currentMonthIndex, 1);
    const lastCompletedStart = new Date(currentYear, currentMonthIndex - 1, 1);

    const monthList = [];

    while (cursor < startOfThisMonth && monthList.length < maxMonthsFromStart) {
        const year = cursor.getFullYear();
        const month = cursor.getMonth();

        const filterRangeStart = new Date(year, month, 1);
        const filterRangeEnd = new Date(year, month + 1, 0);

        const monthObj = {
            monthName: cursor.toLocaleString('default', { month: 'long' }) + ' ' + year,
            filterRangeStart: filterRangeStart.toISOString(),
            filterRangeEnd: filterRangeEnd.toISOString(),
            daysInMonth: filterRangeEnd.getDate(),
            isCurrentMonth:
                year === lastCompletedStart.getFullYear() && month === lastCompletedStart.getMonth(),
        };

        monthList.push(monthObj);

        cursor = new Date(year, month + 1, 1);
    }

    // Newest month first (2026 at top)
    monthList.reverse();

    const currentMonthObj =
        monthList.find((m) => m.isCurrentMonth) || (monthList.length ? monthList[0] : null);

    return { currentMonth: currentMonthObj, monthList };
}

/** Native controls — global `span { color:#000 }` makes MUI/Ant contained buttons look fully black. */
export const payoutNativeBtnStyle = ({ bg, color, border, disabled } = {}) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    background: disabled ? '#e5e7eb' : (bg || '#fff'),
    color: disabled ? '#9ca3af' : (color || '#1e293b'),
    border: `1px solid ${disabled ? '#d1d5db' : (border || '#cbd5e1')}`,
    borderRadius: 6,
    padding: '8px 14px',
    fontWeight: 700,
    fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    textDecoration: 'none',
    lineHeight: 1.2,
});

export const settlementSheetLinkStyle = (tone = 'current') => {
    const isCurrent = tone === 'current';
    return {
        display: 'inline-flex',
        alignItems: 'center',
        color: isCurrent ? '#1d4ed8' : '#9a3412',
        background: isCurrent ? '#dbeafe' : '#ffedd5',
        border: `1px solid ${isCurrent ? '#93c5fd' : '#fdba74'}`,
        borderRadius: 6,
        padding: '6px 12px',
        fontWeight: 700,
        fontSize: 12,
        textDecoration: 'none',
        lineHeight: 1.2,
    };
};

export function SettlementSheetLink({ href, children, tone = 'current' }) {
    if (!href) return null;
    return (
        <a href={href} target="_blank" rel="noreferrer" style={settlementSheetLinkStyle(tone)}>
            {children}
        </a>
    );
}

export function ShowMonthList({ currentMonth, monthList, selectedMonth, setSelectedMonth, walletVisiblePeriod }) {
    const visiblePeriod = String(walletVisiblePeriod || '').trim().toLowerCase();
    return <div style={{ width: 200, display: 'flex', flexDirection: 'column', gap: 4, height: '80vh', overflow: 'scroll' }}>
        {monthList?.map((_month) => {
            const isWalletVisible = visiblePeriod && String(_month.monthName || '').trim().toLowerCase() === visiblePeriod;
            const isSelected = selectedMonth?.monthName === _month?.monthName;
            return (
            <div key={_month.filterRangeStart} onClick={() => { setSelectedMonth(_month); }}>
                <p style={{
                    border: '1px solid #eee', padding: '7px 12px', borderRadius: '0.3em', fontWeight: '600', fontSize: 17, cursor: 'pointer', color: 'slategray',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                    ...(isSelected ? { backgroundColor: 'blue', color: 'white' } : {}),
                }}>
                    <span>
                        {_month.monthName} {_month?.monthName === currentMonth?.monthName && <span style={{ fontWeight: '500', fontSize: 12 }}>***</span>}
                    </span>
                    {isWalletVisible ? (
                        <span style={{
                            fontSize: 10,
                            fontWeight: 700,
                            background: isSelected ? '#bbf7d0' : '#dcfce7',
                            color: '#166534',
                            borderRadius: 999,
                            padding: '2px 6px',
                            whiteSpace: 'nowrap',
                        }}>Wallet</span>
                    ) : null}
                </p>
            </div>
            );
        })}
    </div>;
}
